import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ShoppingBag,
  Heart,
  CheckCircle2,
  BookOpen,
  ArrowUpRight,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PurchaseDialog from "@/components/PurchaseDialog";
import SalesCounter from "@/components/SalesCounter";
import Testimonials from "@/components/sections/Testimonials";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

function BookVisual({ book }) {
  if (book.cover_image) {
    return (
      <img
        src={book.cover_image}
        alt={book.title}
        className="block w-full h-full object-cover"
      />
    );
  }
  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ backgroundColor: book.cover_color || "#0B1E36" }}
    >
      <div className="absolute inset-4 border border-[#C5A059]/40" />
      <div className="absolute inset-0 p-8 flex flex-col justify-between">
        <p className="text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059]">
          Carlos Medeiros
        </p>
        <h3 className="font-serif-display text-3xl text-[#C5A059] leading-tight">
          {book.title}
        </h3>
        <span className="font-serif-display text-lg text-[#C5A059] self-end">
          ✦
        </span>
      </div>
    </div>
  );
}

export default function BookDetail() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get(`/books/${slug}`), api.get(`/books`)])
      .then(([{ data: b }, { data: all }]) => {
        setBook(b);
        setRelated(all.filter((x) => x.slug !== slug).slice(0, 3));
      })
      .catch(() => setErr("Livro não encontrado."))
      .finally(() => setLoading(false));
  }, [slug]);

  const handlePurchaseSuccess = (purchase) => {
    setBook((b) =>
      b ? { ...b, sales_count: (b.sales_count || 0) + 1 } : b
    );
  };

  return (
    <div data-testid="book-detail-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <main className="pt-24">
        {loading && (
          <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
            <div className="h-10 bg-[#F5F7FA] w-64 animate-pulse" />
            <div className="mt-8 grid md:grid-cols-12 gap-12">
              <div className="md:col-span-5 aspect-[2/3] bg-[#F5F7FA] animate-pulse" />
              <div className="md:col-span-7 space-y-4">
                <div className="h-12 bg-[#F5F7FA] w-3/4 animate-pulse" />
                <div className="h-6 bg-[#F5F7FA] w-1/2 animate-pulse" />
                <div className="h-4 bg-[#F5F7FA] w-full animate-pulse" />
              </div>
            </div>
          </div>
        )}

        {!loading && err && (
          <div className="max-w-3xl mx-auto px-6 md:px-8 py-40 text-center">
            <p className="eyebrow">404</p>
            <h1 className="mt-6 font-serif-display text-5xl text-[#0B1E36]">
              {err}
            </h1>
            <Link to="/#livros" className="mt-10 inline-flex btn-secondary-dark">
              Ver todos os livros
            </Link>
          </div>
        )}

        {!loading && book && (
          <>
            {/* Hero */}
            <section
              data-testid="book-hero"
              className="relative pt-6 pb-16 md:pb-24 lg:pb-28 bg-[#FAFAFA] overflow-hidden"
            >
              <div className="max-w-7xl mx-auto px-6 md:px-8">
                <Link
                  to="/#livros"
                  data-testid="book-back-link"
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#111827]/70 hover:text-[#C5A059] transition-colors duration-300"
                >
                  <ArrowLeft size={14} strokeWidth={1.5} />
                  Voltar aos livros
                </Link>

                <div className="mt-12 grid md:grid-cols-12 gap-12 lg:gap-20 items-start">
                  {/* Cover */}
                  <div className="md:col-span-5 lg:col-span-5">
                    <div className="sticky top-28">
                      <div className="relative bg-[#0B1E36] aspect-[3/4] md:aspect-[2/3] shadow-[0_50px_100px_-30px_rgba(11,30,54,0.5)] overflow-hidden">
                        <BookVisual book={book} />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="md:col-span-7 lg:col-span-7">
                    <div className="flex items-center flex-wrap gap-3">
                      <span className="eyebrow eyebrow-gold">
                        {book.category}
                      </span>
                      {book.featured && (
                        <span className="text-[0.65rem] uppercase tracking-[0.25em] bg-[#0B1E36] text-white px-3 py-1.5">
                          Em destaque
                        </span>
                      )}
                    </div>

                    <h1 className="mt-8 font-serif-display text-[#0B1E36] text-[2.75rem] sm:text-5xl md:text-6xl lg:text-[5rem] leading-[1.0] tracking-[-0.032em] font-medium">
                      {book.title}
                    </h1>

                    {book.subtitle && (
                      <p className="mt-7 font-serif-display italic text-[1.2rem] md:text-[1.45rem] text-[#0B1E36]/80 leading-[1.45] tracking-[-0.012em] max-w-2xl">
                        {book.subtitle}
                      </p>
                    )}

                    <div className="mt-10 flex items-baseline gap-6">
                      <span className="font-serif-display text-[2rem] text-[#C5A059]">
                        {book.price}
                      </span>
                      <span className="text-xs uppercase tracking-[0.25em] text-[#6B7280]">
                        E-book · PDF
                      </span>
                    </div>

                    <SalesCounter count={book.sales_count} seed={book.slug} className="mt-5" />

                    <Testimonials
                      variant="compact"
                      className="mt-10 pt-8 border-t border-[#E5E7EB]"
                    />

                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                      <button
                        onClick={() => {
                          track("book_buy_init", {
                            slug: book.slug,
                            price_cents: book.price_cents,
                            location: "book_detail",
                          });
                          setBuyOpen(true);
                        }}
                        data-testid="book-buy-now"
                        className="btn-primary-gold"
                      >
                        <ShoppingBag size={16} strokeWidth={1.5} />
                        Quero adquirir este livro
                      </button>
                      <a href="#sobre-o-livro" className="btn-secondary-dark">
                        <BookOpen size={16} strokeWidth={1.5} />
                        Sobre o livro
                      </a>
                    </div>

                    <div className="mt-12 pt-6 border-t border-[#E5E7EB] flex items-start gap-3">
                      <Heart
                        size={16}
                        strokeWidth={1.5}
                        className="text-[#C5A059] mt-1 flex-shrink-0"
                      />
                      <p className="text-sm md:text-[0.95rem] text-[#6B7280] leading-[1.75] font-light">
                        A aquisição deste livro contribui com a missão e o
                        impacto social do{" "}
                        <span className="text-[#0B1E36] font-medium">
                          Instituto Garoto da Arena
                        </span>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* About the book */}
            <section
              id="sobre-o-livro"
              data-testid="book-about"
              className="bg-white py-20 md:py-28 lg:py-32 border-t border-[#E5E7EB]"
            >
              <div className="max-w-3xl mx-auto px-6 md:px-8">
                <p className="eyebrow">Sobre o livro</p>
                <h2 className="mt-6 font-serif-display text-[#0B1E36] text-[2.25rem] md:text-[2.75rem] lg:text-[3.25rem] leading-[1.05] tracking-[-0.025em] font-medium">
                  Uma leitura que atravessa a mesa, o coração e o propósito.
                </h2>
                <div className="mt-10 devotional-prose">
                  {book.full_description.split("\n\n").map((p, i) => (
                    <p
                      key={i}
                      dangerouslySetInnerHTML={{
                        __html: p.replace(
                          /\*(.+?)\*/g,
                          '<em class="font-serif-display italic text-[#0B1E36]">$1</em>'
                        ),
                      }}
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* Benefits */}
            {book.benefits?.length > 0 && (
              <section
                data-testid="book-benefits"
                className="bg-[#FAFAFA] py-20 md:py-28 lg:py-32"
              >
                <div className="max-w-5xl mx-auto px-6 md:px-8">
                  <p className="eyebrow">O que você vai aprender</p>
                  <h2 className="mt-6 font-serif-display text-[#0B1E36] text-[2rem] md:text-[2.5rem] lg:text-[3rem] leading-[1.08] tracking-[-0.022em] font-medium max-w-2xl">
                    Sete chaves que transformam conversas em acordos reais.
                  </h2>
                  <ul className="mt-14 grid md:grid-cols-2 gap-x-10 gap-y-6">
                    {book.benefits.map((b, i) => (
                      <li
                        key={i}
                        className="flex gap-4 items-start border-l-2 border-[#C5A059]/40 pl-5 py-1"
                      >
                        <CheckCircle2
                          size={18}
                          strokeWidth={1.5}
                          className="text-[#C5A059] mt-1 flex-shrink-0"
                        />
                        <p className="text-[1.02rem] md:text-[1.08rem] text-[#2F374A] leading-[1.7] font-light">
                          {b}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}

            {/* Chapters */}
            {book.chapters?.length > 0 && (
              <section
                data-testid="book-chapters"
                className="bg-white py-20 md:py-28 lg:py-32 border-t border-[#E5E7EB]"
              >
                <div className="max-w-3xl mx-auto px-6 md:px-8">
                  <p className="eyebrow">Capítulos</p>
                  <h2 className="mt-6 font-serif-display text-[#0B1E36] text-[2rem] md:text-[2.5rem] lg:text-[3rem] leading-[1.08] tracking-[-0.022em] font-medium">
                    O que você encontrará dentro.
                  </h2>
                  <ol className="mt-14 divide-y divide-[#E5E7EB] border-t border-[#E5E7EB]">
                    {book.chapters.map((c, i) => (
                      <li
                        key={i}
                        className="py-6 grid grid-cols-12 gap-6 items-baseline"
                      >
                        <span className="col-span-2 md:col-span-1 font-serif-display text-2xl text-[#C5A059]">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="col-span-10 md:col-span-11 font-serif-display text-[1.4rem] md:text-[1.65rem] text-[#0B1E36] leading-tight tracking-[-0.012em]">
                          {c}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              </section>
            )}

            {/* Final CTA */}
            <section
              data-testid="book-final-cta"
              className="relative bg-[#0B1E36] text-white py-20 md:py-28 lg:py-32 overflow-hidden"
            >
              <div className="absolute inset-0 bg-noise" aria-hidden />
              <div
                className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(197,160,89,0.18) 0%, transparent 70%)",
                }}
                aria-hidden
              />
              <div className="relative max-w-4xl mx-auto px-6 md:px-8 text-center">
                <p className="eyebrow eyebrow-gold">Leve para casa</p>
                <h2 className="mt-6 font-serif-display text-[2.5rem] md:text-5xl lg:text-[4.5rem] leading-[1.02] tracking-[-0.03em] font-medium">
                  {book.title}
                </h2>
                <p className="mt-8 max-w-2xl mx-auto text-[1.1rem] md:text-[1.25rem] text-white/70 leading-[1.82] font-light">
                  {book.short_description}
                </p>
                <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                  <button
                    onClick={() => setBuyOpen(true)}
                    data-testid="book-final-cta-buy"
                    className="btn-primary-gold"
                  >
                    <ShoppingBag size={16} strokeWidth={1.5} />
                    Comprar agora · {book.price}
                  </button>
                  <Link to="/#livros" className="btn-ghost-light">
                    Ver outras obras
                    <ArrowUpRight size={14} strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            </section>

            {/* Related */}
            {related.length > 0 && (
              <section
                data-testid="book-related"
                className="bg-[#FAFAFA] py-20 md:py-28 lg:py-32"
              >
                <div className="max-w-7xl mx-auto px-6 md:px-8">
                  <p className="eyebrow">Outras obras</p>
                  <h2 className="mt-6 font-serif-display text-[#0B1E36] text-[2rem] md:text-[2.5rem] lg:text-[3rem] leading-[1.08] tracking-[-0.022em] font-medium">
                    Continue a jornada.
                  </h2>
                  <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
                    {related.map((b) => (
                      <Link
                        key={b.id}
                        to={`/livros/${b.slug}`}
                        data-testid={`related-book-${b.slug}`}
                        className="block group"
                      >
                        <div className="relative aspect-[2/3] overflow-hidden book-cover bg-[#0B1E36]">
                          <BookVisual book={b} />
                        </div>
                        <h4 className="mt-5 font-serif-display text-[1.35rem] text-[#0B1E36] leading-tight tracking-[-0.01em]">
                          {b.title}
                        </h4>
                        <p className="mt-1 text-sm text-[#6B7280] font-light line-clamp-2">
                          {b.short_description}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
      <PurchaseDialog
        book={book}
        open={buyOpen}
        onOpenChange={setBuyOpen}
        onSuccess={handlePurchaseSuccess}
      />
    </div>
  );
}
